import { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import ReCAPTCHA from 'react-google-recaptcha';
import api from './api';
import qs from 'qs';
import { useUser } from './UserContext';
import BusyOverlay from './BusyOverlay';

const Contact = () => {
  const theme = useTheme();
  const { user } = useUser();
  const [recaptchaValue, setRecaptchaValue] = useState('');
  const [form, setForm] = useState({ name: '', email: '', text: '' });
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Auto-fill form fields when user is logged in
  useEffect(() => {
    if (user) {
      setForm(prevForm => ({
        ...prevForm,
        name: user.userFullName || '',
        email: user.userEmail || '',
      }));
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const data = {
      name: form.name,
      email: form.email,
      text: form.text,
      captchaToken: recaptchaValue,
    };

    try {
      setIsLoading(true);
      const res = await api.post('/contactus', qs.stringify(data), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      setIsLoading(false);
      console.log('Success:', res.data);

      setShowSuccess(true);

      setForm(prevForm => ({
        ...prevForm,
        text: '',
      }));
    } catch (err) {
      setIsLoading(false);
      setError(err.response?.data || 'Contact failed');
      console.error('Contact error:', err.response?.data || err.message);
    }
  };

  const isFormValid = form.name && form.email && form.text && recaptchaValue;

  return (
    <>
      <BusyOverlay open={isLoading} message="Sending..." />
      <Box sx={{
        marginBottom: 8,
        marginTop: 8,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        zIndex: 1,
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          opacity: 0.1,
          pointerEvents: 'none',
          zIndex: 0
        }}>
          <img src="/mascot.png" alt="" style={{ width: '100%' }} />
        </div>
        <Paper
          elevation={6}
          sx={{
            padding: 4,
            maxWidth: 500,
            width: '90%',
            textAlign: 'center',
            backgroundColor: theme.palette.background,
            color: theme.palette.text.primary
          }}
        >
          <Box sx={{ marginBottom: 3 }}>
            <Typography sx={{ fontWeight: 'bold', marginRight: 2 }}>CONTACT US</Typography>
          </Box>
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
          {showSuccess && (
            <Typography variant="body2">
              Success!
            </Typography>
          )}
          <form
            onSubmit={handleSubmit}
            style={{
              marginTop: theme.spacing(2),
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(2),
            }}
          >
            <TextField
              required
              label="Name"
              type="text"
              fullWidth
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              inputProps={{ maxLength: 100 }}
            />

            <TextField
              required
              label="Email"
              type="email"
              fullWidth
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              inputProps={{ maxLength: 254 }}
            />

            <TextField
              required
              label="Message"
              multiline
              rows={4}
              fullWidth
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              placeholder="Please describe your question or concern..."
              inputProps={{ maxLength: 5000 }}
            />

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <ReCAPTCHA
                sitekey="6LefeGYrAAAAALRTOHKFlSdFzx9cSnpE6Nt52gcC"
                onChange={(value) => setRecaptchaValue(value)}
              />
            </div>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!isFormValid}
              style={{ color: theme.palette.primary.contrastText }}
            >
              SUBMIT
            </Button>
          </form>
        </Paper>
      </Box>
    </>
  );
};

export default Contact;